<template>
  <div>
    <template v-if="!modalOnly">
      <BaseListItemPicker
        :value="value"
        :button-text="buttonText"
        :clearable="clearable"
        :disabled="disabled"
        :items="decoratedInstitutions"
        :label="label"
        :multiple="multiple"
        :value-field="valueField"
        :text-field="textFields"
        :secondary-fields-in-parentheses="secondaryFieldsInParentheses"
        label-cols="2"
        @open="modal = true"
        @input="$emit('input', $event)"
      />
    </template>

    <BaseModal :is-open="modal || isOpen" :title="buttonText" width="1000px" @close="closeModal">
      <form class="clearfix" @submit.prevent="getInstitutions(1)">
        <ul class="filter_form clearfix">
          <li class="filter_form__items">
            <label class="filter_form__label">{{
              $t('components.institutionPicker.filters.name')
            }}</label>
            <input v-model="params.name" type="text" class="form-control" />
          </li>
          <li v-if="!hideIdFilter" class="filter_form__items">
            <label class="filter_form__label">{{
              $t('components.institutionPicker.filters.institution')
            }}</label>
            <input v-model="params.institution" type="text" class="form-control" />
          </li>
          <li class="filter_form__items">
            <label class="filter_form__label">{{
              $t('components.institutionPicker.filters.jar')
            }}</label>
            <input v-model="params.jar" type="text" class="form-control" />
          </li>

          <li class="filter_form__items filter_form__items--full">
            <button
              :disabled="loading"
              type="submit"
              class="btn btn-feat btn-mobile-full pull-right"
            >
              {{ $t('actions.updateResults') }}
            </button>
          </li>
        </ul>
      </form>

      <p v-if="showDescription" style="margin-bottom: 30px;">
        {{ $t('components.institutionPicker.description') }}
      </p>

      <BaseTable
        :items="institutions"
        :loading="loading"
        :pagination="pagination"
        @update:page="getInstitutions"
      >
        <template #headers>
          <tr>
            <th></th>
            <th>{{ $t('components.institutionPicker.table.headers.name') }}</th>
            <th>{{ $t('components.institutionPicker.table.headers.code') }}</th>
            <th v-if="!hideExtraHeaders">
              {{ $t('components.institutionPicker.table.headers.vatCode') }}
            </th>
            <th v-if="!hideExtraHeaders">
              {{ $t('components.institutionPicker.table.headers.email') }}
            </th>
            <th v-if="!hideExtraHeaders">
              {{ $t('components.institutionPicker.table.headers.phone') }}
            </th>
            <th>{{ $t('components.institutionPicker.table.headers.address') }}</th>
          </tr>
        </template>

        <tr
          v-for="institution in decoratedInstitutions"
          :class="{
            selected: selectedInstitutionSet.has(institution[valueField]),
          }"
          :key="institution[valueField]"
          style="cursor: pointer;"
          @click="toggleInstitution(institution)"
        >
          <td data-label="">
            <input
              :checked="selectedInstitutionSet.has(institution[valueField])"
              :id="`institution-${institution[valueField]}`"
              type="checkbox"
            /><label :for="`institution-${institution[valueField]}`" @click.prevent></label>
          </td>
          <td :data-label="$t('components.institutionPicker.table.headers.name')">
            {{ institution.name }}
          </td>
          <td :data-label="$t('components.institutionPicker.table.headers.code')">
            {{ institution.code }}
          </td>
          <td
            v-if="!hideExtraHeaders"
            :data-label="$t('components.institutionPicker.table.headers.vatCode')"
          >
            {{ institution.vatCode }}
          </td>
          <td
            v-if="!hideExtraHeaders"
            :data-label="$t('components.institutionPicker.table.headers.email')"
          >
            {{ institution.email }}
          </td>
          <td
            v-if="!hideExtraHeaders"
            :data-label="$t('components.institutionPicker.table.headers.phone')"
          >
            {{ institution.phone }}
          </td>
          <td :data-label="$t('components.institutionPicker.table.headers.address')">
            {{ institution.fullAddress }}
          </td>
        </tr>
      </BaseTable>

      <template #footer>
        <a href="#" class="btn btn-feat btn-mobile-full" @click.prevent="closeModal">
          <span class="glyphicon glyphicon-check" aria-hidden="true"></span>
          {{ $t('common.done') }}
        </a>
      </template>
    </BaseModal>
  </div>
</template>

<script>
import BaseListItemPicker from './base/BaseListItemPicker';
import BaseModal from './base/BaseModal';
import institutionService from '../services/institution.service';
import BaseTable from './base/BaseTable';

export default {
  name: 'InstitutionPicker',

  components: { BaseTable, BaseModal, BaseListItemPicker },

  props: {
    value: {
      type: [Array, Object],
      default: () => null,
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: '',
    },
    valueField: {
      type: String,
      default: 'institutionId',
    },
    textFields: {
      type: Array,
      default: () => ['name', 'code', 'fullAddress'],
    },
    modalOnly: {
      type: Boolean,
      default: false,
    },
    isOpen: {
      type: Boolean,
      default: false,
    },
    secondaryFieldsInParentheses: {
      type: Boolean,
      default: false,
    },
    showDescription: {
      type: Boolean,
      default: false,
    },
    hideIdFilter: {
      type: Boolean,
      default: false,
    },
    hideExtraHeaders: {
      type: Boolean,
      default: false,
    },
    appendHasRepRuleParam: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      loading: false,
      modal: false,
      institutions: [],
      params: {},
      pagination: {},
    };
  },

  computed: {
    decoratedInstitutions() {
      return this.institutions.map((institution) => ({
        ...institution,
        fullAddress: institution.address?.fullAddress || '',
      }));
    },

    buttonText() {
      return this.$t(
        `components.institutionPicker.labels.selectInstitution${this.multiple ? 's' : ''}`
      );
    },

    multiple() {
      return this.value && typeof this.value === 'object' && this.value.length >= 0;
    },

    selectedInstitutionSet() {
      if (this.multiple) {
        return new Set(this.value.map((r) => r[this.valueField]));
      }
      if (this.value) {
        return new Set([this.value[this.valueField]]);
      }
      return new Set();
    },
  },

  created() {
    this.getInstitutions();
  },

  methods: {
    closeModal() {
      this.modal = false;
      this.$emit('close');
    },

    getInstitutions(page = 1) {
      const params = { ...this.params, page };
      if (this.appendHasRepRuleParam) {
        params.hasRepRule = true;
      }
      this.loading = true;
      institutionService
        .getInstitutions(params)
        .then((res) => {
          this.institutions = res.data.institution.map((i) => ({
            ...i,
            institutionId: i.id,
          }));
          this.pagination = {
            page: res.data.page,
            total: res.data.total,
            pageSize: res.data.pageSize,
          };
        })
        .catch(() => {
          this.institutions = [];
          this.institutions = {};
        })
        .finally(() => {
          this.loading = false;
        });
    },

    toggleInstitution(institution) {
      let newValue;
      if (this.multiple) {
        newValue = this.selectedInstitutionSet.has(institution[this.valueField])
          ? this.value.filter((i) => i[this.valueField] !== institution[this.valueField])
          : [...this.value, institution];
      } else {
        newValue =
          this.value && this.value[this.valueField] === institution[this.valueField]
            ? null
            : institution;
      }
      this.$emit('input', newValue);
    },
  },
};
</script>
